import { datadogRum } from '@datadog/browser-rum';

interface RumConfig {
  service: string;
}

let rumEnabled = false;

/**
 * Initialize Datadog Browser RUM.
 *
 * Call at module scope (outside components) to guarantee init runs before
 * any React lifecycle. Safe to call multiple times — only the first call
 * takes effect. No-ops when VITE_ENABLE_RUM is not 'true' or required
 * config is missing.
 */
export function initRum(config: RumConfig): void {
  if (rumEnabled) return;

  const enabled = import.meta.env.VITE_ENABLE_RUM;
  if (enabled !== 'true') return;

  const applicationId = import.meta.env.VITE_DD_APPLICATION_ID;
  const clientToken = import.meta.env.VITE_DD_CLIENT_TOKEN;
  const site = import.meta.env.VITE_DD_SITE;

  if (!applicationId || !clientToken || !site) {
    console.warn('[Datadog RUM] Missing required env vars (VITE_DD_APPLICATION_ID, VITE_DD_CLIENT_TOKEN, VITE_DD_SITE). Skipping init.');
    return;
  }

  datadogRum.init({
    applicationId,
    clientToken,
    site,
    service: config.service,
    env: import.meta.env.VITE_ENVIRONMENT || 'development',
    version: import.meta.env.VITE_APP_VERSION || '0.0.0',
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    trackResources: true,
    trackUserInteractions: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
  });

  rumEnabled = true;
}

/**
 * Set the authenticated user and account/tenant context on the active RUM session.
 */
export function setRumUser(
  userId: string,
  tenantId: string,
  options?: { tenantName?: string; userName?: string; userEmail?: string },
): void {
  if (!rumEnabled) return;
  const user: { id: string; name?: string; email?: string } = { id: userId };
  if (options?.userName) user.name = options.userName;
  if (options?.userEmail) user.email = options.userEmail;
  datadogRum.setUser(user);
  const account: { id: string; name?: string } = { id: tenantId };
  if (options?.tenantName) account.name = options.tenantName;
  datadogRum.setAccount(account);
}

/**
 * Clear user and account context (e.g. on logout).
 */
export function clearRumUser(): void {
  if (!rumEnabled) return;
  datadogRum.clearUser();
  datadogRum.clearAccount();
}

/**
 * Send a custom product-analytics event to Datadog (RUM custom action).
 * No-ops when RUM is not initialized.
 */
export function sendEvent(name: string, attributes?: Record<string, unknown>): void {
  if (!rumEnabled) return;
  datadogRum.addAction(name, attributes);
}
